import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AnalysesMethodsDetails, DepthMeasurementType, ExperimentModel, FinalReportDetailsModel, FinalSampleInputsModel, SamplingInformation, SamplingOpeningRow } from '../../models/sample';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { SharedService } from '../../services/shared.service';
import { Subscription } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MESSAGES_CONSTANTS } from '../../common/messages-constants';
import { NUMBERS_CONSTANTS } from '../../common/numbers-constants';
import { AnalysesMethodsConstants, cancelDialogTemplate, checkboxWarningDialogTemplate, deleteCrewDialogTemplate, ImageConstants, recombineDisplayColumns, recombineHeaders, saveAction, updateAction } from '../../common/texts-constants';
import { CVD_CONSTANTS, reportConstants, roleIds } from '../../common/constants';
import { PROJECT_CONSTANTS } from '../../common/experiment-constants';
import { FinalSampleInputsService } from '../../services/final-sample-inputs.service';
import { SampleCreationService } from '../../services/sample-creation.service';
import { MaterialDesignModule } from '../../common/material-design/material-design.module';
import { SharedModule } from '../../directives/SharedModule';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ElementRef, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
  selector: 'app-final-sample-inputs',
  templateUrl: './final-sample-inputs.component.html',
  styleUrls: ['./final-sample-inputs.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialDesignModule,
    SharedModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ]
})

export class FinalSampleInputsComponent implements OnInit, OnDestroy {
  readonly roleIds = roleIds;
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private finalSampleInputsService = inject(FinalSampleInputsService);
  private toastr = inject(ToastrService);
  private sharedService = inject(SharedService);
  private sampleService = inject(SampleCreationService);
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  finalSampleInput!: FormGroup;
  frontPageForm!: FormGroup;
  coverLetterForm!: FormGroup;
  analysesMethodsForm!: FormGroup;
  projectId = NUMBERS_CONSTANTS.zero;
  projectNumber = '';
  labTestId = NUMBERS_CONSTANTS.zero;
  userId = '';
  activeTab = '';
  roleId = '';
  dataLoaded = false;
  finalSampleInputId: number = NUMBERS_CONSTANTS.zero;
  finalReportDetailsId: number = NUMBERS_CONSTANTS.zero;
  isFinalSampleInputEditMode = false;
  isFinalReportDetailsEditMode = false;
  labTestIdToEdit?: number;
  finalSampleInputsModel: FinalSampleInputsModel | undefined;
  finalReportDetailsModel: FinalReportDetailsModel | undefined;
  depthMeasurementTypes: DepthMeasurementType[] = [];
  selectedTab = NUMBERS_CONSTANTS.zero;
  selectedIndex: number = NUMBERS_CONSTANTS.zero;
  private userDetailSub!: Subscription;
  private userRoleSub!: Subscription;
  private sampleInfoSub!: Subscription;
  headers: string[] = recombineHeaders;
  displayColumns: string[] = recombineDisplayColumns;

  dataSource = new MatTableDataSource<SamplingOpeningRow>([]);
  samplingOpeningRows: SamplingOpeningRow[] = [];
  selection = new SelectionModel<SamplingOpeningRow>(true);
  @ViewChild(cancelDialogTemplate) cancelDialog!: TemplateRef<unknown>;
  @ViewChild(checkboxWarningDialogTemplate) checkboxWarningDialog!: TemplateRef<unknown>;
  @ViewChild(deleteCrewDialogTemplate) deleteCrewDialog!: TemplateRef<unknown>;
  @ViewChild('byInfoButton') byInfoButton!: ElementRef<HTMLElement>;
  @ViewChild('byInfoTemplate', { static: true }) byInfoTemplate!: TemplateRef<unknown>;
  byOverlayRef!: OverlayRef;
  pendingCrewDeleteIndex = -NUMBERS_CONSTANTS.one;
  exxonmobilBanner!: string;
  energyBanner!: string;
  officeImage!: string;
  reportSampleType: string | undefined;
  wellName: string | undefined;
  reservoirName: string | undefined;
  analysesMethodsConstants = AnalysesMethodsConstants;
  reportConstants = reportConstants;
  analysesMethodsDetailsId: number = NUMBERS_CONSTANTS.zero;
  experimentNames: string[] = [];
  experimentList: ExperimentModel[] = [];
  enterInitialsCommaSeparatedMessage = MESSAGES_CONSTANTS.enterInitalsCommaSeparated;
  ngOnInit(): void {
    this.projectId = this.route.snapshot.params[PROJECT_CONSTANTS.projectId] ? this.route.snapshot.params[PROJECT_CONSTANTS.projectId] : NUMBERS_CONSTANTS.zero;
    this.projectNumber = this.route.snapshot.params[PROJECT_CONSTANTS.projectNumber] ? this.route.snapshot.params[PROJECT_CONSTANTS.projectNumber] : '';
    this.labTestId = this.route.snapshot.params[PROJECT_CONSTANTS.labTestId] ? this.route.snapshot.params[PROJECT_CONSTANTS.labTestId] : NUMBERS_CONSTANTS.zero;
    this.userDetailSub = this.sharedService.userDetail$.subscribe((userDetail) => {
      if (userDetail) {
        this.userId = userDetail?.mail;
      }
    });
    this.userRoleSub = this.sharedService.userRoleId$.subscribe((userRoleId) => {
      if (userRoleId) {
        this.roleId = userRoleId;
        this.ApplyRoleBasedControlState();
      }
    });
    this.sampleInfoSub = this.sampleService.sampleDetails$.subscribe((data) => {
      if (data) {
        this.reportSampleType = data.page1?.reportSampletype;
        this.wellName = data.page1?.wellName;
        this.reservoirName = data.page1?.reservoirName;
      }
    });

    this.exxonmobilBanner = ImageConstants.exxonmobilBanner;
    this.energyBanner = ImageConstants.energyBanner;
    this.officeImage = ImageConstants.officeImage;
    this.InitializeForm();
    this.LoadDepthMeasurementTypes();
    if (this.labTestId > NUMBERS_CONSTANTS.zero) {
      this.getLabTestList();
      this.GetFinalSampleInputs();
      this.GetSamplingInformation();
      this.GetFinalReportDetails();
      this.GetAnalysesMethods();
    } else {
      this.isFinalSampleInputEditMode = false;
      this.isFinalReportDetailsEditMode = false;
    }
  }

  ngOnDestroy(): void {
    this.userDetailSub?.unsubscribe();
    this.userRoleSub?.unsubscribe();
    this.sampleInfoSub?.unsubscribe();
  }

  getLabTestList() {
    this.sampleService.GetLabTestRepeatList(this.labTestId).subscribe((response) => {
      if (response.result !== null) {
        response.result.forEach(element => {
          this.experimentNames.push(element.experimentName);
        });
      }
    });
  }

  AddLabCrew(): void {
    this.LabCrewArray.push(this.fb.control('', { nonNullable: true }));
  }

  InitializeForm(): void {
    this.finalSampleInput = this.fb.group({
      flowingTopDepth: [''],
      flowingBottomDepth: [''],
      depthMeasurementType: [''],
      upperPerforationDepth: [''],
      lowerPerforationDepth: [''],
      volumeTransferred: [''],
      sampleVolume: [''],
      reservoirFluid: [''],
      reservoirAge: [''],
      samplingDepth: [''],
      testType: [''],
      dateSampled: [''],
      samplingCompany: ['']
    })

    this.frontPageForm = this.fb.group({
      productionInterval: [''],
      reportDate: [''],
      labCrew: this.fb.array([
        this.fb.control('', { nonNullable: true })
      ])
    });

    this.coverLetterForm = this.fb.group({
      reportToPrefix: [''],
      reportTo: [''],
      fromAddress1: [''],
      fromAddress2: [''],
      fromAddress3: [''],
      fromAddress4: [''],
      toAddress1: [''],
      toAddress2: [''],
      toAddress3: [''],
      toAddress4: [''],
      comments: [''],
      productionIntervalCover: [''],
      regards: [''],
      by: [''],
      attachment: [''],
    });


    this.analysesMethodsForm = this.fb.group({
      fluidRecombination: [''],
      sampleRestorationTemp: [''],
      sampleRestorationPressure: [''],
      sampleRestorationDays: [''],
      compositionalAnalysis: [''],
      ccePressure: [''],
      cceIsothermic: [''],
      swellingTest: [''],
      cambridgeEMV: [''],
    });

    this.ApplyRoleBasedControlState();
  }

  /** Single source of truth for role-restricted controls; safe to call before forms exist. */
  private ApplyRoleBasedControlState(): void {
    const isTechResource = this.roleId === this.roleIds.techResource;
    const restrictedControls = [
      this.finalSampleInput?.get('depthMeasurementType'),
      this.finalSampleInput?.get('dateSampled'),
      this.frontPageForm?.get('reportDate'),
      this.coverLetterForm?.get('reportToPrefix')
    ];
    restrictedControls.forEach((control) => {
      if (!control) {
        return;
      }
      if (isTechResource) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  LoadDepthMeasurementTypes(): void {
    this.finalSampleInputsService.GetDepthMeasurementTypes().subscribe({
      next: (depthMeasurementTypes) => {
        this.depthMeasurementTypes = depthMeasurementTypes;
        if (!depthMeasurementTypes.length) {
          this.toastr.error(MESSAGES_CONSTANTS.failedToLoadSamplingTypes);
        }
      }
    });
  }

  GetFinalSampleInputs() {
    this.finalSampleInputsService.GetFinalSampleInputs(this.labTestId).subscribe((data) => {
      if (data) {
        this.BindFinalSampleInputs(data);
      }
    });
  }

  GetSamplingInformation() {
    this.finalSampleInputsService.GetSamplingInformationRows(this.labTestId).subscribe((rows) => {
      this.samplingOpeningRows = rows;
      this.dataSource.data = rows;
    });
  }

  GetFinalReportDetails(): void {
    this.finalSampleInputsService.GetFinalReportDetails(this.labTestId).subscribe((data) => {
      if (data) {
        this.BindFinalReportDetails(data);
      }
    });
  }

  ShowInputSave(): boolean {
    return !this.isFinalSampleInputEditMode;
  }

  ShowInputUpdate(): boolean {
    return this.isFinalSampleInputEditMode;
  }

  ShowReportDetailsSave(): boolean {
    return !this.isFinalReportDetailsEditMode;
  }

  ShowReportDetailsUpdate(): boolean {
    return this.isFinalReportDetailsEditMode;
  }

  GoBackToEditSample() {
    this.sharedService.NavigateToEditSample(this.projectId, this.projectNumber, this.labTestId);
  }

  OnCancel(): void {
    // If the form has no changes, navigate away directly.
    if (this.selectedIndex === NUMBERS_CONSTANTS.one || (this.selectedIndex === NUMBERS_CONSTANTS.zero && this.finalSampleInput.pristine)) {
      this.sharedService.NavigateToEditSample(this.projectId, this.projectNumber, this.labTestId);
      return;
    }

    // Otherwise, ask for confirmation.
    const dialogRef = this.dialog.open(this.cancelDialog, {
      width: CVD_CONSTANTS.n700px,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.sharedService.NavigateToEditSample(this.projectId, this.projectNumber, this.labTestId);
      }
    });
  }

  ValidateSelectedCount(event: MatCheckboxChange) {
    if (!event.checked) {
      return true;
    }

    this.samplingOpeningRows = this.dataSource.data as SamplingOpeningRow[];
    const canSelect = this.finalSampleInputsService.CanSelectRecombine(this.samplingOpeningRows);
    if (!canSelect) {
      this.dialog.open(this.checkboxWarningDialog, {
        width: CVD_CONSTANTS.n700px,
      });
      event.source.checked = false;
      return false;
    }

    return true;
  }

  SaveSamplingInformation() {
    this.samplingOpeningRows = this.dataSource.data as SamplingOpeningRow[];

    const toNumberOrNull = (val: unknown): number | null => {
      if (val === null || val === undefined) return null;

      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed === '') return null;

        const num = Number(trimmed);
        return Number.isNaN(num) ? null : num;
      }

      const num = Number(val);
      return Number.isNaN(num) ? null : num;
    };

    const rows: SamplingOpeningRow[] = this.samplingOpeningRows.map(row => ({
      ...row,
      closingTemperature: toNumberOrNull(row.closingTemperature),
      closingPressure: toNumberOrNull(row.closingPressure)
    }));

    const samplingInformation: SamplingInformation = {
      labTestId: this.labTestId,
      userId: this.userId,
      rows: rows
    };

    this.finalSampleInputsService.SaveSamplingInformation(samplingInformation)
      .subscribe({
        next: (res) => {
          if (res?.isSuccess && res.result) {
            this.toastr.success(MESSAGES_CONSTANTS.dataSavedSuccessfully);
          } else {
            this.toastr.error(res?.displayMessage || MESSAGES_CONSTANTS.failedToSaveData);
          }
        },
        // HTTP interceptor already shows the toast and logs the error; nothing left to do here.
        error: () => undefined,
      });
  }


  toggleRow(row: SamplingOpeningRow): void {
    row.isRecombine = !row.isRecombine;
    this.selection.toggle(row);
  }

  SaveFinalInputsData(action: typeof saveAction | typeof updateAction): void {
    this.finalSampleInputsService
      .SaveFinalSampleInputs(action, this.finalSampleInput?.value, this.finalSampleInputId, this.labTestId, this.userId)
      .subscribe({
        next: (response) => {
          if (response?.isSuccess) {
            this.toastr.success(
              action === saveAction
                ? MESSAGES_CONSTANTS.dataSavedSuccessfully
                : MESSAGES_CONSTANTS.dataUpdatedSuccessfully
            );
            // Always fetch from server to keep UI consistent with backend state
            this.GetFinalSampleInputs();
          } else {
            this.toastr.error(response?.displayMessage || MESSAGES_CONSTANTS.operationFailed);
          }
        },
        // HTTP interceptor already shows the toast and logs the error; nothing left to do here.
        error: () => undefined,
      });
  }

  BindFinalSampleInputs(model: FinalSampleInputsModel) {
    if (model !== undefined) {
      this.finalSampleInputId = model.finalSampleInputId ?? NUMBERS_CONSTANTS.zero;
      this.isFinalSampleInputEditMode = true;
      this.finalSampleInput.patchValue(this.finalSampleInputsService.ToFinalSampleInputFormValue(model));
    }
  }

  SaveFinalReportDetailsData(action: typeof saveAction | typeof updateAction): void {
    const isCoverLetterTab = this.selectedIndex === NUMBERS_CONSTANTS.three;

    this.finalSampleInputsService
      .SaveFinalReportDetails(
        action,
        this.frontPageForm.getRawValue(),
        isCoverLetterTab ? this.coverLetterForm.getRawValue() : {},
        this.LabCrewValues,
        this.finalReportDetailsId,
        this.labTestId,
        this.userId,
        isCoverLetterTab,
        this.finalReportDetailsModel
      )
      .subscribe({
        next: (response) => {
          if (response?.isSuccess) {
            this.toastr.success(
              action === saveAction
                ? MESSAGES_CONSTANTS.dataSavedSuccessfully
                : MESSAGES_CONSTANTS.dataUpdatedSuccessfully
            );
            this.GetFinalReportDetails();
          } else {
            this.toastr.error(response?.displayMessage || MESSAGES_CONSTANTS.operationFailed);
          }
        },
        // HTTP interceptor already shows the toast and logs the error; nothing left to do here.
        error: () => undefined,
      });
  }

  BindFinalReportDetails(model: FinalReportDetailsModel): void {
    this.finalReportDetailsModel = model;

    const binding = this.finalSampleInputsService.ToFinalReportDetailsBindingState(model);

    this.isFinalReportDetailsEditMode = binding.isEditMode;
    this.finalReportDetailsId = binding.finalReportDetailsId;

    this.frontPageForm.patchValue(binding.frontPagePatchValue);
    this.coverLetterForm.patchValue(binding.coverLetterPatchValue);

    binding.crewList.forEach((value, index) => {
      const crewControl = this.LabCrewArray.at(index);
      if (crewControl) {
        crewControl.setValue(value, { emitEvent: false });
      } else {
        this.LabCrewArray.push(this.fb.control(value, { nonNullable: true }));
      }
    });

    while (this.LabCrewArray.length > binding.crewList.length) {
      this.LabCrewArray.removeAt(this.LabCrewArray.length - NUMBERS_CONSTANTS.one);
    }
  }

  ConfirmRemoveCrew(index: number): void {
    this.pendingCrewDeleteIndex = index;

    const dialogRef = this.dialog.open(this.deleteCrewDialog, {
      width: CVD_CONSTANTS.n700px,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result === true) {
        this.RemoveCrewRowByIndex(this.pendingCrewDeleteIndex);
      }
      this.pendingCrewDeleteIndex = -NUMBERS_CONSTANTS.one;
    });
  }

  RemoveCrewRowByIndex(index: number): void {
    const fa = this.LabCrewArray;
    if (!fa) { return; }

    if (fa.length > NUMBERS_CONSTANTS.one) {
      fa.removeAt(index);
    } else {
      fa.at(NUMBERS_CONSTANTS.zero).setValue('');
    }
  }

  get LabCrewArray(): FormArray<FormControl<string>> {
    return this.frontPageForm.get('labCrew') as FormArray<FormControl<string>>;
  }

  get LabCrewValues(): string[] {
    return this.LabCrewArray.getRawValue();
  }

  ShowSeparatorForm(): boolean {
    return this.reportSampleType?.toLowerCase() === reportConstants.reportTypeSeparatorTest;
  }

  ShowMDTForm(): boolean {
    return this.reportSampleType?.toLowerCase() === reportConstants.reportTypeMDT;
  }

  ShowDSTForm(): boolean {
    return this.reportSampleType?.toLowerCase() === reportConstants.reportTypeDST;
  }

  ShowAnalyseMethodsSave(): boolean {
    return this.selectedIndex === NUMBERS_CONSTANTS.four && !this.HasAnyAnalyseMethodsId();
  }

  ShowAnalyseMethodsUpdate(): boolean {
    return this.selectedIndex === NUMBERS_CONSTANTS.four && this.HasAnyAnalyseMethodsId();
  }

  private HasAnyAnalyseMethodsId(): boolean {
    return this.analysesMethodsDetailsId > 0;
  }

  BindAnalysesMethodsData(data: AnalysesMethodsDetails): void {
    if (!data) {
      return;
    }

    this.analysesMethodsDetailsId = data.analysesMethodsDetailsId ?? 0;

    this.analysesMethodsForm.patchValue({
      fluidRecombination: data.fluidRecombination,
      sampleRestorationTemp: data.sampleRestorationTemp,
      sampleRestorationPressure: data.sampleRestorationPressure,
      sampleRestorationDays: data.sampleRestorationDays,
      compositionalAnalysis: data.compositionalAnalysis,
      ccePressure: data.ccePressure,
      cceIsothermic: data.cceIsothermic,
      swellingTest: data.swellingTest,
      cambridgeEMV: data.cambridgeEMV
    });
  }

  SaveAnalysesMethods(action: typeof saveAction | typeof updateAction): void {
    const formValue = this.analysesMethodsForm.getRawValue();

    const payload: AnalysesMethodsDetails = {
      analysesMethodsDetailsId: this.analysesMethodsDetailsId,
      labTestId: this.labTestId,
      fluidRecombination: formValue.fluidRecombination,
      sampleRestorationTemp: formValue.sampleRestorationTemp,
      sampleRestorationPressure: formValue.sampleRestorationPressure,
      sampleRestorationDays: formValue.sampleRestorationDays,
      compositionalAnalysis: formValue.compositionalAnalysis,
      ccePressure: formValue.ccePressure,
      cceIsothermic: formValue.cceIsothermic,
      swellingTest: formValue.swellingTest,
      cambridgeEMV: formValue.cambridgeEMV,
      createdUserId: this.userId,
      ...(this.analysesMethodsDetailsId > 0 ? { updatedUserId: this.userId } : {})
    };

    this.sampleService.SaveAnalysesMethodsDetails(payload).subscribe({
      next: (res) => {
        if (res?.isSuccess) {
          this.toastr.success(
            action === saveAction
              ? MESSAGES_CONSTANTS.dataSavedSuccessfully
              : MESSAGES_CONSTANTS.dataUpdatedSuccessfully
          );
          this.GetAnalysesMethods();
        } else {
          this.toastr.error(res?.displayMessage || MESSAGES_CONSTANTS.operationFailed);
        }
      },
      // HTTP interceptor already shows the toast and logs the error; nothing left to do here.
      error: () => undefined,
    });
  }

  GetAnalysesMethods(): void {
    this.sampleService.GetAnalysesMethodsDetails(this.labTestId).subscribe(res => {
      if (res?.isSuccess && res.result) {
        this.BindAnalysesMethodsData(res.result);
      }
    });
  }

  PreventInvalidNumberInput(event: KeyboardEvent, col: string): void {
    if (col !== 'closingTemperature' && col !== 'closingPressure') {
      return;
    }

    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  ShowByInfo(): void {
    if (this.byOverlayRef?.hasAttached()) {
      return;
    }
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.byInfoButton)
      .withPositions([
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center'
        }
      ]);
    if (!this.byOverlayRef) {
      this.byOverlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: false
      });
    }
    this.byOverlayRef.attach(
      new TemplatePortal(
        this.byInfoTemplate,
        this.viewContainerRef
      )
    );
  }

  HideByInfo(): void {
    this.byOverlayRef?.detach();
  }
}
